import React, { useEffect } from 'react';
import { Route, Routes, useLocation } from 'react-router';
import { useLocalStorageState } from 'ahooks';
import { AccountPersistInfo } from 'Internal.CommonAPI';
import ErrorBoundary from 'Common/pages/error';
import withWaitingComponent from 'Common/HOC/withWaitingComponent';
import Loading from 'Common/Components/Loading';
import {
  useLatestSubscription,
  useOrganizationToken,
} from './hooks/business/subscription';
import { useAccountingSetup } from './hooks/accounting/setup';

const LegacyBusinessScreen = React.lazy(
  () =>
    import(
      /* webpackChunkName: "businessSettingsModule" */
      'Settings/screens/LegacyBusiness'
    )
);

const BusinessScreen = React.lazy(
  () =>
    import(
      /* webpackChunkName: "businessSettingsModule" */
      'Settings/screens/Business'
    )
);

const EcommerceScreen = React.lazy(
  () =>
    import(
      /* webpackChunkName: "webshopSettingsModule" */
      'Settings/screens/Ecommerce'
    )
);

const AccountingScreen = React.lazy(
  () =>
    import(
      /* webpackChunkName: "accountingSettingsModule" */
      'Settings/screens/Accounting'
    )
);

const StoreScreen = React.lazy(
  () =>
    import(
      /* webpackChunkName: "storeSettingsModule" */
      'Settings/screens/Store'
    )
);

const CustomerDisplayScreen = React.lazy(
  () =>
    import(
      /* webpackChunkName: "customerDisplaySettingsModule" */
      'Settings/screens/CustomerDisplay'
    )
);
const SettingsRouter = () => {
  const [account] = useLocalStorageState<AccountPersistInfo>('account');
  const [preAccounting, setPreAccounting] =
    useLocalStorageState<boolean>('pre-accounting');
  const location = useLocation();
  const orgTokenQuery = useOrganizationToken({
    enabled: location.pathname === '/settings/general',
  });
  const preAccountingQuery = useAccountingSetup();

  const latestSubscriptionQuery = useLatestSubscription(
    orgTokenQuery.data?.org_token,
    account?.merchant_id
  );

  useEffect(() => {
    if (preAccounting === undefined) {
      preAccountingQuery.refetch();
    }
  }, [preAccounting, preAccountingQuery]);

  useEffect(() => {
    if (preAccountingQuery) {
      setPreAccounting(preAccountingQuery.isSuccess);
    }
  }, [preAccountingQuery, setPreAccounting]);

  const getGeneralScreenComponent = () => {
    const conditions = {
      hasError:
        orgTokenQuery.status === 'error' ||
        orgTokenQuery.data === undefined ||
        latestSubscriptionQuery.status === 'error' ||
        latestSubscriptionQuery.data === undefined ||
        latestSubscriptionQuery.data === null,
    };

    if (
      orgTokenQuery.isLoading ||
      (orgTokenQuery.status === 'success' && latestSubscriptionQuery.isLoading)
    ) {
      return <Loading noText verticallyCentered />;
    }

    if (conditions.hasError) {
      return withWaitingComponent(LegacyBusinessScreen);
    }
    return withWaitingComponent(BusinessScreen);
  };

  return (
    <Routes>
      <Route path="general" element={getGeneralScreenComponent()} />
      <Route
        path="e-commerce"
        element={withWaitingComponent(EcommerceScreen)}
      />
      <Route
        path="accounting"
        element={
          preAccounting ? (
            withWaitingComponent(AccountingScreen)
          ) : (
            <ErrorBoundary />
          )
        }
      />
      <Route path="store" element={withWaitingComponent(StoreScreen)} />
      {account !== undefined && (
        <Route
          path="customer-display"
          element={
            account.isPlusUser ? (
              withWaitingComponent(CustomerDisplayScreen)
            ) : (
              <ErrorBoundary />
            )
          }
        />
      )}
      <Route path="*" element={<ErrorBoundary />} />
    </Routes>
  );
};

export default SettingsRouter;
